import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import _ from 'lodash'
import translate from 'libs/translate'
import { getConnectorIcon } from '../libs/connectors'
import { makeLinksClickable } from '../libs/misc'

import './style.scss'

const Info = ({ children, title }) => {
    return (
        children.length ? <div>
            <div className='LocationDetailSidebar__sidebar__content__details'>
                <h2>{title}</h2>
                <div
                    className='LocationDetailSidebar__sidebar__content__details__text'
                >
                    {children}
                </div>
            </div>
        </div> : <span />
    )
}
Info.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string
}

const LocationDetailSidebar = ({ onTabClick, data = {}, status }) => {
    const parsedInfo = (rawInfo) => makeLinksClickable(rawInfo)

    return (
        <div className={`LocationDetailSidebar ${status}`}>
            <div onClick={onTabClick} className='LocationDetailSidebar__tab' />
            <div className='LocationDetailSidebar__sidebar'>
                <div className='LocationDetailSidebar__sidebar__content'>
                    <TokenNotSupportedWarning show={data.unsupported} />
                    <div className='LocationDetailSidebar__sidebar__content__address'>
                        {data.fullAddress}
                    </div>
                    <Connectors data={data.connectorGroups} />
                    <AccessHours data={data.access} />
                    <Details operator={data.operator} code={data.code} address={data.fullAddress} />
                    <Info title={translate('landing_sidebar_info')}>{parsedInfo(data.info)}</Info>
                    <Info title={translate('landing_sidebar_accessibility_remarks')}>{parsedInfo(data.accessibilityRemark)}</Info>
                </div>
            </div>
        </div>
    )
}
LocationDetailSidebar.propTypes = {
    status: PropTypes.string,
    data: PropTypes.object,
    onTabClick: PropTypes.func
}

const ConnectorGroup = ({ data }) => {
    return (
        <div>
            <div className='LocationDetailSidebar__sidebar__content__connectors'>
                <Connector data={data.summary.big} type={data.type} big />
                {_.map(data.summary.small, (d) => { return <Connector data={d} key={Math.random()} type={data.type} /> })}

                {(data.power)
                    ? <div className='max-power'>{translate('landing_sidebar_max_power')} <span className='value'>{data.power} kW</span></div>
                    : <div />}
            </div>
            {(data.pricingDetails && _.isArray(data.pricingDetails.fees) && (data.pricingDetails.fees.length > 0))
                ? <PricingDetails />
                : <span />}
        </div>
    )
}
ConnectorGroup.propTypes = {
    data: PropTypes.object
}

const Connector = ({ big, type, data }) => {
    const sizeClass = ((big) ? 'big' : 'small')
    const className = classNames('connector', { [data.statusClass]: true, [sizeClass]: true })
    return (<div className={className}>
        <img src={getConnectorIcon(type).default} />
        {' '}{data.amount} {(big) ? data.status : ''}
    </div>)
}
Connector.propTypes = {
    data: PropTypes.object,
    type: PropTypes.string,
    big: PropTypes.bool
}

const PricingDetails = () => {
    return (<div className='LocationDetailSidebar__sidebar__content__pricing-details'>
        <h2>{translate('landing_sidebar_pricing_details')}</h2>
        <p>{translate('landing_sidebar_pricing_details_refer_to_app')}</p>
    </div>)
}

const TableRow = ({ label, value }) => {
    return (<tr><td>{label}</td><td>{value}</td></tr>)
}
TableRow.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
}

const BlockingFee = ({ data }) => {
    const {perMinuteMinDuration: minDuration, perMinute: pricePerMinute, perMinuteMaxPrice: maxPrice} = data

    let blockingFeeNote = translate('landing_map_price_blocking_fee_appendix_main')
        .replace(/Pre\+Fill\+Price/g, pricePerMinute)
        .replace(/Pre\+Fill\+Time/g, minDuration)

    if (maxPrice) {
        blockingFeeNote += ' ' + translate('landing_map_price_blocking_fee_appendix_additional')
            .replace(/Pre\+Fill\+Price/g, maxPrice)
    }

    blockingFeeNote += '.'

    return (
        <div className='blocking-fee'>
            <div className='info-icon'></div>
            <div className='note'>{blockingFeeNote}</div>
        </div>
    )
}
BlockingFee.propTypes = {
    data: PropTypes.object
}

const Connectors = ({ data }) => {
    return (
        <div className='Connectors'>
            {_.map(data, (g) => { return <ConnectorGroup key={g.groupId} data={g} /> })}
        </div>
    )
}
Connectors.propTypes = {
    data: PropTypes.array,
}

const AccessHours = ({ data }) => {
    return (<div className='LocationDetailSidebar__sidebar__content__access-hours'>
        <Show if={data && data.length === 0}>
            <div className='not-available'>{translate('landing_sidebar_unknown')}</div>
        </Show>
        <h2>{translate('landing_sidebar_access_hours')}</h2>
        <table>
            <tbody>
                {_.map(data, (p) => { return <TableRow key={Math.random()} label={p.weekDays} value={p.range} /> })}
            </tbody>
        </table>
    </div>)
}
AccessHours.propTypes = {
    data: PropTypes.array,
}

const Details = ({ operator, code }) => {
    return (<div className='LocationDetailSidebar__sidebar__content__details'>
        <h2>{translate('landing_sidebar_details')}</h2>
        <p>{translate('landing_sidebar_owner')} {operator}</p>
        <p>{translate('landing_sidebar_code')} {code}</p>
        {/* See NO_JIRA_remove_report_an_issue_link commit message to revert the hide of the
        cp-report-a-problem when the link is fixed. see
         https://thenewmotion.slack.com/archives/C02TPK8KBLH/p1699968575253739*/} 
    </div>)
}
Details.propTypes = {
    operator: PropTypes.string,
    code: PropTypes.string,
    address: PropTypes.string
}

const TokenNotSupportedWarning = ({ show }) => {
    return (
        <Show if={show}>
            <div className='LocationDetailSidebar__sidebar__content__not-supported-by-card'>
                {translate('landing_sidebar_chargepoint_not_supported')}
            </div>
        </Show>
    )
}
TokenNotSupportedWarning.propTypes = {
    show: PropTypes.bool
}

const Show = ({ if: showIf, children }) => showIf ? children : <span />
Show.propTypes = {
    if: PropTypes.bool,
    children: PropTypes.node,
}

export default LocationDetailSidebar
